import { Box, Flex, Text } from '@chakra-ui/react';
import { Pagination, Accordion } from '@morressier/alembic';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import {
  fetchGlobalReviewerSearchConfig,
  fetchReviewerSearchProperties,
  fetchSearchQueryResults,
  IDynamicFilters,
  IFilters,
  ISearchCriteria,
  ISortType
} from 'src/api/queries';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { useEffect, useMemo, useRef, useState } from 'react';
import SearchResults from './SearchResults';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { getDateFilterCriteria } from '../../../../utils/date';
import { RecommendedReviewers } from './RecommendedReviewers';
import AdvancedSearch from './AdvancedSearch';
import {
  IDependentValue,
  ILexiconField,
  IReviewerRecommendation,
  RecommendationProvider
} from '@publishing-engine/domain/models';
import ExternalRecommendations from './ExternalRecommendations/ExternalRecommendations';

export type ISelectedFacets = Record<string, string[]>;
type IActiveFilterChip = {
  key: string;
  label: string;
  value: string;
};

type AccordionSection =
  | 'recommended'
  | 'opposed'
  | 'prophy'
  | 'globalCampus'
  | 'webOfScience'
  | 'search';

const ReviewerSearch = () => {
  const { submissionContextId, submissionId, fieldsResponse, organizationId } =
    useSubmissionData();
  const { submissionContext } = useSubmissionContextData();
  const prophyEnabled =
    submissionContext?.config?.reviewerRecommendationProviders?.some(
      p => p.provider === 'PROPHY' && p.enable
    );
  const globalCampusEnabled =
    submissionContext?.config?.reviewerRecommendationProviders?.some(
      p => p.provider === 'GLOBAL_CAMPUS' && p.enable
    );
  const webOfScienceEnabled =
    submissionContext?.config?.reviewerRecommendationProviders?.some(
      p => p.provider === 'WEB_OF_SCIENCE' && p.enable
    );
  const [openSection, setOpenSection] = useState<AccordionSection | null>(
    'search'
  );
  const controlledAccordion = (section: AccordionSection) => ({
    allowMultiple: true,
    index: openSection === section ? [0] : [],
    onChange: (idx: number[]) =>
      setOpenSection(idx.includes(0) ? section : null)
  });
  const lexiconProjectId = submissionContext?.config?.lexiconProjectId ?? '';
  const lexiconClassId = submissionContext?.config?.lexiconClassId ?? '';
  const reviewerConfig = submissionContext?.config?.cms?.lexicon?.fields;
  const dateFilters = submissionContext?.config?.cms?.lexicon?.dateFilters;
  const userFilters = submissionContext?.config?.cms?.lexicon?.userFilter;
  const globalLexiconConfig = submissionContext?.config?.globalLexiconConfig;
  const ADVANCED_SEARCH_FIELD_ORDER = [
    'AcceptedNoReview',
    'ActiveReviews',
    'Agreed',
    'AverageReviewerTurnaroundTime',
    'AverageRScore',
    'LastInvitedDate',
    'PercentAcceptedNoReview',
    'PercentInvitedNoReview',
    'ReviewerFirstName',
    'ReviewerInstitution',
    'ReviewerKeyword',
    'ReviewerLastName',
    'ReviewerOnboardedDate',
    'ReviewerPersonDesignation',
    'ReviewerSpecialty',
    'scandinavianKeywords',
    'TotalOpenInvites'
  ];

  const getAdvancedSearchFields = (fields?: ILexiconField[]) =>
    (fields?.filter(f => f.advancedSearch !== false) ?? []).sort((a, b) => {
      const aIndex = ADVANCED_SEARCH_FIELD_ORDER.indexOf(a.predicateLabel);
      const bIndex = ADVANCED_SEARCH_FIELD_ORDER.indexOf(b.predicateLabel);
      return (
        (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) -
        (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex)
      );
    });

  const advancedSearchFields = getAdvancedSearchFields(
    submissionContext?.config?.cms?.lexicon?.fields
  );

  const { data: globalReviewerSearchConfig } = useQuery({
    queryKey: ['global-reviewer-search-config', organizationId],
    queryFn: () =>
      organizationId ? fetchGlobalReviewerSearchConfig(organizationId) : null,
    enabled: !!organizationId && !!globalLexiconConfig
  });

  const globalAdvancedSearchFields = getAdvancedSearchFields(
    globalReviewerSearchConfig?.fields
  );

  const { data: reviewerPropertiesResponse } = useQuery({
    queryKey: [
      'reviewer-search-properties',
      submissionContextId,
      lexiconProjectId,
      lexiconClassId
    ],
    queryFn: () =>
      submissionContextId
        ? fetchReviewerSearchProperties(
            submissionContextId,
            lexiconProjectId,
            lexiconClassId
          )
        : null,
    enabled: !!lexiconProjectId && !!lexiconClassId
  });
  const reviewerProperties = reviewerPropertiesResponse?.journal;
  const globalReviewerProperties = reviewerPropertiesResponse?.global;
  const [page, setPage] = useState<number>(1);
  const size = 10;
  const start = (page - 1) * size;

  const methods = useForm<{ dateValue: string }>({
    defaultValues: {
      dateValue: 'All'
    }
  });

  const { watch } = methods;

  const [searchCriteria, setSearchCriteria] = useState<ISearchCriteria[]>([]);
  const [globalSearchCriteria, setGlobalSearchCriteria] = useState<
    ISearchCriteria[]
  >([]);
  const baseCriteriaRef = useRef<ISearchCriteria[]>([]);

  const [selectedFacets, setSelectedFacets] = useState<ISelectedFacets>({});
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});

  const DEFAULT_SORT = 'ReviewerFirstName_ASC';
  const [sortValue, setSortValue] = useState<string>(DEFAULT_SORT);
  const mapSortValueToSortType = (value: string): ISortType => {
    const [keyRaw, orderRaw] = value.split('_');
    const isRelevance = keyRaw === 'RELEVANCE';

    const key: ISortType['key'] = isRelevance ? 'RELEVANCE' : 'SCHEMA';
    const order: ISortType['order'] =
      orderRaw === 'ASC' || orderRaw === 'DESC' ? orderRaw : 'DESC';

    const id = isRelevance
      ? lexiconClassId
      : reviewerProperties?.[0]?.properties?.find(
          ({ predicateLabel }) => predicateLabel === keyRaw
        )?.id;

    const dataType = isRelevance
      ? 'xsd:string'
      : reviewerProperties?.[0]?.properties?.find(
          ({ predicateLabel }) => predicateLabel === keyRaw
        )?.datatype ?? 'xsd:string';

    return {
      id,
      key,
      order,
      datatype: dataType
    };
  };
  const sortType = mapSortValueToSortType(sortValue);
  const handleSortChange = (newSortValue: string) => {
    setPage(1);
    setSortValue(newSortValue);
  };

  const filters: IFilters = {
    classFilter: [lexiconClassId]
  };

  const userFilterSchemaIds = new Set(
    (userFilters ?? [])
      .map(
        f =>
          reviewerProperties?.[0]?.properties.find(
            p => p.predicateLabel === f.key
          )?.id
      )
      .filter((id): id is string => !!id)
  );

  const userFilterKeys = new Set((userFilters ?? []).map(f => f.key));
  const facetFieldSchemaIds: string[] = (reviewerConfig ?? [])
    .filter(f => f.facet === true && !userFilterKeys.has(f.predicateLabel))
    .map(
      f =>
        reviewerProperties?.[0]?.properties.find(
          p => p.predicateLabel === f.predicateLabel
        )?.id
    )
    .filter((id): id is string => !!id);

  const baseSchemas: string[] = [
    ...facetFieldSchemaIds,
    ...[...userFilterSchemaIds]
  ];

  const dynamicFilters: IDynamicFilters[] = baseSchemas
    .filter(schemaId => schemaId !== '')
    .map(schemaId => {
      const isBoolean = userFilterSchemaIds.has(schemaId);
      return {
        schemaId,
        datatype: isBoolean ? 'xsd:boolean' : 'xsd:string',
        values: selectedFacets[schemaId] ?? []
      };
    });

  const globalUserFilters = globalReviewerSearchConfig?.userFilter;
  const globalReviewerConfig = globalReviewerSearchConfig?.fields;
  const globalUserFilterSchemaIds = new Set(
    (globalUserFilters ?? [])
      .map(
        f =>
          globalReviewerProperties?.[0]?.properties.find(
            p => p.predicateLabel === f.key
          )?.id
      )
      .filter((id): id is string => !!id)
  );

  const globalUserFilterKeys = new Set(
    (globalUserFilters ?? []).map(f => f.key)
  );
  const globalFacetFieldSchemaIds: string[] = (globalReviewerConfig ?? [])
    .filter(
      f => f.facet === true && !globalUserFilterKeys.has(f.predicateLabel)
    )
    .map(
      f =>
        globalReviewerProperties?.[0]?.properties.find(
          p => p.predicateLabel === f.predicateLabel
        )?.id
    )
    .filter((id): id is string => !!id);

  const globalBaseSchemas: string[] = [
    ...globalFacetFieldSchemaIds,
    ...[...globalUserFilterSchemaIds]
  ];

  const globalDynamicFilters: IDynamicFilters[] = globalBaseSchemas
    .filter(schemaId => schemaId !== '')
    .map(schemaId => {
      const isBoolean = globalUserFilterSchemaIds.has(schemaId);
      return {
        schemaId,
        datatype: isBoolean ? 'xsd:boolean' : 'xsd:string',
        values: []
      };
    });

  const selectedDateValue = watch('dateValue');

  const activeFilterChips = useMemo<IActiveFilterChip[]>(() => {
    const schemaIdToPredicateLabel = new Map(
      (reviewerProperties?.[0]?.properties ?? []).map(property => [
        property.id,
        property.predicateLabel
      ])
    );

    const facetFieldLabelByPredicate = new Map(
      (reviewerConfig ?? [])
        .filter(field => field.facet === true)
        .map(field => [field.predicateLabel, field.label])
    );

    const userFilterByPredicate = new Map(
      (userFilters ?? []).map(f => [f.key, f.label])
    );

    const facetChips = Object.entries(selectedFacets)
      .flatMap(([schemaId, values]) => {
        const predicateLabel = schemaIdToPredicateLabel.get(schemaId);
        const fieldLabel = predicateLabel
          ? facetFieldLabelByPredicate.get(predicateLabel)
          : undefined;
        const userFilterLabel = predicateLabel
          ? userFilterByPredicate.get(predicateLabel)
          : undefined;

        const formattedValues = values
          .filter(value => value !== '')
          .map(value =>
            userFilterLabel && value === 'true' ? userFilterLabel : value
          );

        if (formattedValues.length === 0) {
          return [];
        }

        return [
          {
            key: schemaId,
            label: userFilterLabel
              ? 'Users'
              : fieldLabel ?? predicateLabel ?? 'Filter',
            value: formattedValues.join(', ')
          }
        ];
      })
      .reduce<{ key: string; label: string; value: string }[]>((acc, chip) => {
        const existing = acc.find(c => c.label === chip.label);
        if (existing) {
          existing.value = `${existing.value}, ${chip.value}`;
        } else {
          acc.push({ ...chip });
        }
        return acc;
      }, []);

    if (!selectedDateValue || selectedDateValue === 'All') {
      return facetChips;
    }

    const dateLabel =
      dateFilters?.find(f => f.key === selectedDateValue)?.label ??
      selectedDateValue;

    return [
      {
        key: `date-${selectedDateValue}`,
        label: 'Date / Period',
        value: dateLabel
      },
      ...facetChips
    ];
  }, [
    dateFilters,
    userFilters,
    reviewerConfig,
    reviewerProperties,
    selectedDateValue,
    selectedFacets
  ]);

  const buildMergedCriteria = (
    baseCriteria: ISearchCriteria[],
    dateValue: string
  ): ISearchCriteria[] => {
    let criteria = [...baseCriteria];
    if (dateValue && dateValue !== 'All') {
      criteria = [
        ...criteria,
        ...getDateFilterCriteria(dateValue, reviewerProperties, dateFilters)
      ];
    }
    return criteria;
  };

  const handleSearch = (
    advancedCriteria: ISearchCriteria[],
    globalCriteria: ISearchCriteria[]
  ) => {
    baseCriteriaRef.current = advancedCriteria;
    setGlobalSearchCriteria(globalCriteria);
    setSortValue('RELEVANCE_ASC');
    setPage(1);

    const selectedDateValue = methods.getValues('dateValue');
    const mergedCriteria = buildMergedCriteria(
      advancedCriteria,
      selectedDateValue as string
    );
    setSearchCriteria(mergedCriteria);
  };

  const handleSearchReset = () => {
    baseCriteriaRef.current = [];
    setGlobalSearchCriteria([]);
    setSortValue(DEFAULT_SORT);
    setPage(1);
    const selectedDateValue = methods.getValues('dateValue');
    const mergedCriteria = buildMergedCriteria([], selectedDateValue as string);
    setSearchCriteria(mergedCriteria);
  };

  const handleSearchResultsReset = () => {
    methods.setValue('dateValue', 'All');
    setSortValue(DEFAULT_SORT);
    setPage(1);
    setSelectedFacets({});
    setSelectedFilters({});
    const mergedCriteria = buildMergedCriteria(baseCriteriaRef.current, 'All');
    setSearchCriteria(mergedCriteria);
  };

  const handleFiltersSubmit = (filters: Record<string, string[]>) => {
    const { dateValue, ...facets } = filters;
    setPage(1);
    setSelectedFacets(facets);
    setSelectedFilters(filters);
    methods.setValue('dateValue', dateValue?.[0] ?? 'All');
    setSortValue('RELEVANCE_ASC');
    const mergedCriteria = buildMergedCriteria(
      baseCriteriaRef.current,
      dateValue?.[0] ?? 'All'
    );
    setSearchCriteria(mergedCriteria);
  };
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const [totalPages, setTotalPages] = useState(0);

  const {
    data: searchResults,
    isLoading,
    isFetching: fetching
  } = useQuery({
    queryKey: [
      'reviewer-search-results',
      submissionContextId,
      lexiconProjectId,
      start,
      size,
      {
        searchCriteria,
        dynamicFilters,
        sortType,
        filters,
        globalSearchCriteria
      }
    ],
    queryFn: () =>
      fetchSearchQueryResults({
        ctxId: submissionContextId,
        projectId: lexiconProjectId,
        start,
        size,
        sortType,
        searchCriteria,
        dynamicFilters,
        filters,
        fuzzy: false,
        submissionId,
        globalFilters: globalSearchCriteria.length
          ? {
              searchCriteria: globalSearchCriteria,
              dynamicFilters: globalDynamicFilters
            }
          : undefined
      }),
    enabled:
      !!submissionContextId && !!lexiconProjectId && !!reviewerProperties,
    keepPreviousData: true,
    onSuccess: data => {
      const pages = data?.total ? Math.ceil(data.total / size) : 0;
      setTotalPages(pages);
    },
    onError: () => {
      setTotalPages(0);
    }
  });
  const isFetching = isLoading || fetching;
  const filterGroups = useMemo(() => {
    const groups: {
      key: string;
      label: string;
      items: { value: string; name: string }[];
    }[] = [];

    // Date filter group
    if (dateFilters?.length) {
      groups.push({
        key: 'dateValue',
        label: 'Date / Period',
        items: dateFilters.map(f => ({ value: f.key, name: f.label }))
      });
    }

    // User filter groups (one per user filter, keyed by schema ID)
    if (userFilters?.length) {
      for (const uf of userFilters) {
        const schemaId = reviewerProperties?.[0]?.properties.find(
          p => p.predicateLabel === uf.key
        )?.id;
        if (schemaId) {
          groups.push({
            key: schemaId,
            label: uf.label,
            items: [{ value: 'true', name: uf.label }]
          });
        }
      }
    }

    // Facet groups from propertyFacets
    const userFilterKeys = new Set((userFilters ?? []).map(f => f.key));
    const facetFields = (reviewerConfig ?? []).filter(
      f => f.facet === true && !userFilterKeys.has(f.predicateLabel)
    );

    for (const field of facetFields) {
      const matchedFacet = (searchResults?.propertyFacets ?? [])
        .flatMap(g => g.facets)
        .find(
          f => f.propertyName === field.predicateLabel && f.values.length > 0
        );

      if (!matchedFacet) continue;

      groups.push({
        key: matchedFacet.schemaId,
        label: field.label,
        items: matchedFacet.values.map(v => ({
          value: v.key,
          name: `${v.key} (${v.count})`
        }))
      });
    }

    return groups;
  }, [
    dateFilters,
    userFilters,
    searchResults,
    reviewerProperties,
    reviewerConfig
  ]);
  useEffect(() => {
    if (!isFetching && !prophyEnabled && !globalCampusEnabled && loaderRef.current) {
      requestAnimationFrame(() => {
        const element = loaderRef.current;
        if (!element) return;
        const navbar = document.querySelector<HTMLElement>('[data-navbar]');
        const navbarHeight = navbar?.getBoundingClientRect()?.height ?? 0;
        element.style.scrollMarginTop = `${navbarHeight + 3}px`;
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [isFetching, prophyEnabled, globalCampusEnabled]);

  const recommendedReviewers = (
    fieldsResponse?.fields?.RecommendedReviewers?.value as IDependentValue<
      IReviewerRecommendation[]
    > | null
  )?.exists
    ? (
        fieldsResponse?.fields?.RecommendedReviewers?.value as IDependentValue<
          IReviewerRecommendation[]
        >
      ).value ?? []
    : [];

  const opposedReviewers = (
    fieldsResponse?.fields?.OpposedReviewers?.value as IDependentValue<
      IReviewerRecommendation[]
    > | null
  )?.exists
    ? (
        fieldsResponse?.fields?.OpposedReviewers?.value as IDependentValue<
          IReviewerRecommendation[]
        >
      ).value ?? []
    : [];

  return (
    <Flex flexDirection="column">
      {recommendedReviewers.length > 0 && (
        <RecommendedReviewers
          reviewers={recommendedReviewers}
          title="Author - Recommended Reviewers"
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          isOpen={openSection === 'recommended'}
          onToggle={isOpen => setOpenSection(isOpen ? 'recommended' : null)}
        />
      )}
      {opposedReviewers.length > 0 && (
        <RecommendedReviewers
          reviewers={opposedReviewers}
          title="Author - Opposed Reviewers"
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          isOpen={openSection === 'opposed'}
          onToggle={isOpen => setOpenSection(isOpen ? 'opposed' : null)}
        />
      )}

      {prophyEnabled && (
        <ExternalRecommendations
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          provider={RecommendationProvider.PROPHY}
          title="Prophy Recommended Reviewers"
          isOpen={openSection === 'prophy'}
          onToggle={isOpen => setOpenSection(isOpen ? 'prophy' : null)}
        />
      )}

      {globalCampusEnabled && (
        <ExternalRecommendations
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          provider={RecommendationProvider.GLOBAL_CAMPUS}
          title="Global Campus Recommended Reviewers"
          isOpen={openSection === 'globalCampus'}
          onToggle={isOpen => setOpenSection(isOpen ? 'globalCampus' : null)}
        />
      )}

      {webOfScienceEnabled && (
        <ExternalRecommendations
          submissionContextId={submissionContextId}
          submissionId={submissionId}
          provider={RecommendationProvider.WEB_OF_SCIENCE}
          title="Web of Science Recommended Reviewers"
          isOpen={openSection === 'webOfScience'}
          onToggle={isOpen => setOpenSection(isOpen ? 'webOfScience' : null)}
        />
      )}

      <Flex
        backgroundColor="surfacePrimaryFill"
        ref={loaderRef}
        direction="column"
      >
        <>
          <Accordion
            chakraAccordionProps={controlledAccordion('search')}
            items={[
              {
                accordionItemProps: {
                  marginBottom: 0.5
                },
                title: (
                  <Text
                    textStyle={'headingItem'}
                  >{`All Reviewers - Advanced Search`}</Text>
                ),
                content: (
                  <Box>
                    <Box
                      borderBottom="1px solid"
                      borderColor={'neutralPrimaryBorder'}
                      padding={3}
                    >
                      <AdvancedSearch
                        advancedSearchFields={advancedSearchFields}
                        reviewerProperties={reviewerProperties}
                        reviewerConfig={reviewerConfig}
                        submissionContextId={submissionContextId}
                        lexiconProjectId={lexiconProjectId}
                        isFetching={isFetching}
                        globalAdvancedSearchFields={globalAdvancedSearchFields}
                        onSearch={handleSearch}
                        globalReviewerProperties={globalReviewerProperties}
                        onReset={handleSearchReset}
                        globalLexiconConfig={globalLexiconConfig}
                      />{' '}
                    </Box>
                    <SearchResults
                      activeFilterChips={activeFilterChips}
                      searchResults={searchResults}
                      sortValue={sortValue}
                      setSortValue={handleSortChange}
                      reviewerConfig={reviewerConfig}
                      globalLexiconConfig={globalLexiconConfig}
                      filterGroups={filterGroups}
                      watch={watch as any}
                      onFiltersSubmit={handleFiltersSubmit}
                      selectedFilters={selectedFilters}
                      setSelectedFilters={setSelectedFilters}
                      isFetching={isFetching}
                      onReset={handleSearchResultsReset}
                    />

                    {totalPages > 1 && (
                      <Flex
                        justifyContent="center"
                        py={4}
                        key={`${totalPages}-${page}`}
                      >
                        <Pagination
                          ariaLabel="Notes pagination"
                          currentPage={page}
                          totalPages={totalPages}
                          onPageChange={setPage}
                        />
                      </Flex>
                    )}
                  </Box>
                )
              }
            ]}
          />
        </>
      </Flex>
    </Flex>
  );
};

export default ReviewerSearch;
